import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { bookings, contacts } from '@repletoryreach/sdk-api';
import { useDebounce } from '@repletoryreach/sdk-utils/hooks';
import { Button, Group, Stack, Text, Title } from '@repletoryreach/sdk-ui';
import { Alert } from '@repletoryreach/sdk-ui/data-display';
import { Modal } from '@repletoryreach/sdk-ui/overlays';
import { Select, Switch, Textarea, TextInput } from '@repletoryreach/sdk-ui/inputs';
import { useT } from '../../../i18n/I18nProvider';
import { BOOKINGS_KEY, bookingErrorText, useServices, useStaff } from '../lib/data';
import { AvailabilityPick, AvailabilityPicker } from './AvailabilityPicker';
import { QuestionField, missingAnswers, type Answer } from './QuestionField';

export interface NewBookingPrefill {
  /** Local date to open the picker on. */
  date?: string;
  /** Local `HH:mm` to choose when it is free. */
  time?: string;
  staffId?: string;
  serviceId?: string;
  contact?: { name?: string; phone?: string; email?: string; ref?: string; contact_id?: string };
  conversationId?: string;
  channel?: string;
}

const MUTED = 'var(--rr-color-text-muted)';

/**
 * Book for a client: the service, then a day and a time from the service's own
 * availability, then who the client is and the service's questions. Used from
 * the calendar, the bookings list and an inbox conversation (which fills the
 * client in and links the booking to the chat).
 */
export function NewBookingModal({ opened, onClose, prefill, tz, onBooked }: {
  opened: boolean;
  onClose: () => void;
  prefill?: NewBookingPrefill;
  tz: string;
  onBooked?: (b: bookings.Booking) => void;
}) {
  const t = useT();
  const qc = useQueryClient();
  const servicesQ = useServices();
  const staffQ = useStaff();
  const services = (servicesQ.data?.data ?? []).filter((s) => s.active);
  const staff = staffQ.data?.data ?? [];

  const [serviceId, setServiceId] = useState('');
  const [staffId, setStaffId] = useState('');
  const [pick, setPick] = useState<AvailabilityPick | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [contactId, setContactId] = useState<string | undefined>();
  const [address, setAddress] = useState('');
  const [answers, setAnswers] = useState<Record<string, Answer | undefined>>({});
  const [notes, setNotes] = useState('');
  const [notify, setNotify] = useState(true);
  const [search, setSearch] = useState('');
  const [tried, setTried] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [booked, setBooked] = useState<bookings.Booking | null>(null);
  const debounced = useDebounce(search, 250);

  // A fresh form each time it opens, filled from what opened it.
  useEffect(() => {
    if (!opened) return;
    setServiceId(prefill?.serviceId ?? '');
    setStaffId(prefill?.staffId ?? '');
    setPick(null);
    setName(prefill?.contact?.name ?? '');
    setPhone(prefill?.contact?.phone ?? '');
    setEmail(prefill?.contact?.email ?? '');
    setContactId(prefill?.contact?.contact_id);
    setAddress('');
    setAnswers({});
    setNotes('');
    setNotify(true);
    setSearch('');
    setTried(false);
    setError(null);
    setBooked(null);
  }, [opened]); // eslint-disable-line react-hooks/exhaustive-deps

  // One service: choose it for them.
  useEffect(() => {
    if (opened && !serviceId && services.length === 1) setServiceId(services[0]!.id);
  }, [opened, services.length]); // eslint-disable-line react-hooks/exhaustive-deps

  const service = services.find((s) => s.id === serviceId) ?? null;
  const people = service ? staff.filter((p) => p.active && service.staff_ids.includes(p.id)) : [];
  const choosePerson = service?.kind === 'appointment' && service.staff_mode === 'choose';
  const needsAddress = !!service && (service.kind === 'callout' || service.location_kind === 'client');

  const contactsQ = useQuery({
    queryKey: ['contacts', 'booking-search', debounced],
    queryFn: () => contacts.listContacts({ search: debounced.trim(), limit: 6 }),
    enabled: opened && debounced.trim().length >= 2,
    retry: false
  });

  const missing = service ? missingAnswers(service.questions, answers) : [];
  const nameOk = name.trim().length > 0;
  const reachOk = phone.trim().length > 0 || email.trim().length > 0 || !!prefill?.contact?.ref;
  const ready = !!service && !!pick && nameOk && reachOk && missing.length === 0 && (!needsAddress || address.trim().length > 0);

  const create = useMutation({
    mutationFn: () => {
      const staffForBooking = pick?.staff_id ?? (staffId || (choosePerson ? pick?.staff_ids?.[0] : undefined));
      const clean = Object.fromEntries(Object.entries(answers).filter(([, v]) => v !== undefined && v !== '')) as Record<string, Answer>;
      return bookings.createBooking({
        service_id: service!.id,
        starts_at: pick!.session_id ? undefined : pick!.starts_at,
        session_id: pick!.session_id,
        staff_id: staffForBooking ?? undefined,
        contact: {
          name: name.trim(),
          phone: phone.trim() || undefined,
          email: email.trim() || undefined,
          contact_id: contactId,
          ref: prefill?.contact?.ref
        },
        address: needsAddress ? address.trim() : undefined,
        answers: Object.keys(clean).length > 0 ? clean : undefined,
        notes: notes.trim() || undefined,
        channel: prefill?.channel ?? 'console',
        conversation_id: prefill?.conversationId,
        notify_client: notify
      });
    },
    onSuccess: (res) => {
      void qc.invalidateQueries({ queryKey: [BOOKINGS_KEY] });
      setBooked(res.data);
      onBooked?.(res.data);
    },
    onError: (e) => {
      const code = bookings.bookingErrorCode(e);
      setError(bookingErrorText(t, e));
      // The time has gone: ask again and let them choose another.
      if (code === bookings.SLOT_TAKEN || code === bookings.FULL) {
        setPick(null);
        setRefreshKey((k) => k + 1);
      }
    }
  });

  function submit() {
    setTried(true);
    setError(null);
    if (!ready) return;
    create.mutate();
  }

  const serviceOptions = useMemo(
    () => [{ value: '', label: t('bookings.new.chooseService') }, ...services.map((s) => ({ value: s.id, label: s.name }))],
    [services, t]
  );

  if (booked) {
    return (
      <Modal opened={opened} onClose={onClose} title={t('bookings.new.doneTitle')} size="sm">
        <Stack gap="md">
          <Stack gap="xs">
            <Text role="caption" c={MUTED}>{t('bookings.field.number')}</Text>
            <Title order={3} data-testid="booked-number">{booked.number}</Title>
            <Text role="bodySmall">{booked.when_text}</Text>
            <Text role="bodySmall" c={MUTED}>
              {booked.status === 'requested' ? t('bookings.new.doneRequested') : t('bookings.new.doneConfirmed')}
            </Text>
          </Stack>
          <Group gap="sm" justify="flex-end">
            <Link to={`/bookings/list?booking=${encodeURIComponent(booked.id)}`} onClick={onClose}>
              <Button size="sm" variant="default">{t('bookings.new.openBooking')}</Button>
            </Link>
            <Button size="sm" onClick={onClose}>{t('action.close')}</Button>
          </Group>
        </Stack>
      </Modal>
    );
  }

  return (
    <Modal opened={opened} onClose={onClose} title={t('bookings.new.title')} size="lg">
      <form onSubmit={(e) => { e.preventDefault(); submit(); }}>
        <Stack gap="md">
          {servicesQ.error && <Alert color="error" role="alert">{bookingErrorText(t, servicesQ.error)}</Alert>}
          {!servicesQ.isPending && services.length === 0 ? (
            <Alert color="info">
              <Stack gap="xs">
                <span>{t('bookings.new.noServices')}</span>
                <div><Link to="/bookings/services" onClick={onClose}><Button size="xs" variant="default">{t('bookings.services.addFirst')}</Button></Link></div>
              </Stack>
            </Alert>
          ) : (
            <>
              <Select
                label={t('bookings.field.service')}
                value={serviceId}
                onChange={(e) => { setServiceId(e.currentTarget.value); setPick(null); setAnswers({}); }}
                data={serviceOptions}
                error={tried && !service ? t('bookings.new.serviceRequired') : undefined}
                required
              />

              {service && choosePerson && people.length > 0 && (
                <Select
                  label={t('bookings.field.person')}
                  value={staffId}
                  onChange={(e) => { setStaffId(e.currentTarget.value); setPick(null); }}
                  data={[{ value: '', label: t('bookings.new.anyone') }, ...people.map((p) => ({ value: p.id, label: p.name }))]}
                  description={t('bookings.new.personHint')}
                />
              )}

              {service && (
                <AvailabilityPicker
                  key={`${service.id}:${staffId}`}
                  service={service}
                  staffId={staffId || undefined}
                  tz={tz}
                  value={pick}
                  onChange={(p) => { setPick(p); setError(null); }}
                  initialDate={prefill?.date}
                  initialTime={prefill?.time}
                  refreshKey={refreshKey}
                />
              )}
              {tried && service && !pick && <Text role="caption" c="var(--rr-color-error)">{t('bookings.new.timeRequired')}</Text>}

              <Stack gap="sm">
                <Text role="inputLabel">{t('bookings.field.client')}</Text>
                {!prefill?.contact && (
                  <Stack gap="xs">
                    <TextInput
                      label={t('bookings.new.findContact')}
                      value={search}
                      onChange={(e) => setSearch(e.currentTarget.value)}
                      onKeyDown={(e) => { if (e.key === 'Enter') e.preventDefault(); }}
                      description={t('bookings.new.findContactHint')}
                      autoComplete="off"
                    />
                    {(contactsQ.data?.data ?? []).length > 0 && (
                      <div className="rr-bookings__contact-hits">
                        {(contactsQ.data?.data ?? []).map((c) => (
                          <button
                            key={c.id}
                            type="button"
                            className="rr-bookings__contact-hit"
                            onClick={() => {
                              setName(c.name ?? '');
                              setPhone(c.msisdn ?? '');
                              setEmail(c.email ?? '');
                              setContactId(c.id);
                              setSearch('');
                            }}
                          >
                            <span>{c.name || c.msisdn || c.email}</span>
                            <span className="rr-bookings__time-hint">{[c.msisdn, c.email].filter(Boolean).join(' · ')}</span>
                          </button>
                        ))}
                      </div>
                    )}
                  </Stack>
                )}
                <TextInput
                  label={t('bookings.field.clientName')}
                  value={name}
                  onChange={(e) => { setName(e.currentTarget.value); setContactId(undefined); }}
                  error={tried && !nameOk ? t('bookings.new.nameRequired') : undefined}
                  required
                />
                <Group gap="sm" grow wrap="wrap" align="flex-start">
                  <TextInput
                    type="tel"
                    label={t('bookings.field.phone')}
                    value={phone}
                    onChange={(e) => setPhone(e.currentTarget.value)}
                    description={t('bookings.new.phoneHint')}
                    error={tried && !reachOk ? t('bookings.new.reachRequired') : undefined}
                  />
                  <TextInput
                    type="email"
                    label={t('common.email')}
                    value={email}
                    onChange={(e) => setEmail(e.currentTarget.value)}
                  />
                </Group>
              </Stack>

              {needsAddress && (
                <Textarea
                  label={t('bookings.field.address')}
                  value={address}
                  onChange={(e) => setAddress(e.currentTarget.value)}
                  minRows={2}
                  required
                  error={tried && !address.trim() ? t('bookings.new.addressRequired') : undefined}
                />
              )}

              {service && service.questions.length > 0 && (
                <Stack gap="sm">
                  <Text role="inputLabel">{t('bookings.field.answers')}</Text>
                  {service.questions.map((q) => (
                    <QuestionField
                      key={q.key}
                      question={q}
                      value={answers[q.key]}
                      onChange={(v) => setAnswers((a) => ({ ...a, [q.key]: v }))}
                      error={tried && missing.includes(q.key) ? t('bookings.new.answerRequired') : undefined}
                    />
                  ))}
                </Stack>
              )}

              <Textarea
                label={t('bookings.field.notes')}
                value={notes}
                onChange={(e) => setNotes(e.currentTarget.value)}
                minRows={2}
                maxLength={2000}
                description={t('bookings.new.notesHint')}
              />

              <Switch
                label={t('bookings.field.notifyClient')}
                description={t('bookings.new.notifyHint')}
                checked={notify}
                onChange={setNotify}
              />

              {error && <Alert color="error" role="alert">{error}</Alert>}

              <Group gap="sm" justify="flex-end">
                <Button variant="default" onClick={onClose}>{t('action.cancel')}</Button>
                <Button type="submit" loading={create.isPending}>{t('bookings.new.submit')}</Button>
              </Group>
            </>
          )}
        </Stack>
      </form>
    </Modal>
  );
}
